import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';

// Admin user IDs - add your admin user IDs here
const ADMIN_USER_IDS = [
  // Add admin user IDs here, e.g.:
  // 'uuid-of-admin-user-1',
  // 'uuid-of-admin-user-2',
];

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const AdminReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, isDark } = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'accepted', 'declined', 'all'
  const [processing, setProcessing] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        navigate('/');
        return;
      }

      // Get user's profile ID
      const { data: profile } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (profile && ADMIN_USER_IDS.includes(profile.id)) {
        setIsAdmin(true);
      } else {
        // For development, allow access if no admins are configured
        if (ADMIN_USER_IDS.length === 0) {
          setIsAdmin(true);
        } else {
          navigate('/');
        }
      }
    };

    checkAdmin();
  }, [user, navigate]);

  // Fetch reports
  useEffect(() => {
    if (!isAdmin) return;

    const fetchReports = async () => {
      setLoading(true);

      let query = supabase
        .from('reports')
        .select(`
          id,
          reporter_id,
          reported_card_id,
          reported_user_id,
          reason,
          description,
          status,
          created_at,
          reviewed_at,
          reporter:users!reports_reporter_id_fkey(id, username, avatar_url),
          card:cards!reports_reported_card_id_fkey(id, name, digital_image_url, original_image_url, user_id),
          reported_user:users!reports_reported_user_id_fkey(id, username, avatar_url)
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error('Error fetching reports:', error);
      } else {
        // Fetch card owner info for each report
        const reportsWithOwners = await Promise.all(
          (data || []).map(async (report) => {
            if (report.card?.user_id) {
              const { data: owner } = await supabase
                .from('users')
                .select('id, username, avatar_url, report_strikes')
                .eq('id', report.card.user_id)
                .single();
              return { ...report, cardOwner: owner };
            }
            return report;
          })
        );
        setReports(reportsWithOwners);
      }

      setLoading(false);
    };

    fetchReports();
  }, [isAdmin, filter]);

  // Accept report
  const handleAccept = async (report) => {
    setProcessing(report.id);

    try {
      // Update report status
      await supabase
        .from('reports')
        .update({ status: 'accepted', reviewed_at: new Date().toISOString() })
        .eq('id', report.id);

      // Card stays hidden
      // Reporter's successful_reports is incremented automatically by DB trigger (on_report_accepted)

      // Increment card owner's report_strikes
      if (report.card?.user_id) {
        await supabase.rpc('increment_field', {
          table_name: 'users',
          field_name: 'report_strikes',
          row_id: report.card.user_id
        }).catch(() => {
          // Fallback if RPC doesn't exist
          supabase
            .from('users')
            .update({ report_strikes: (report.cardOwner?.report_strikes || 0) + 1 })
            .eq('id', report.card.user_id);
        });
      }

      // Update local state
      setReports(prev => prev.map(r =>
        r.id === report.id ? { ...r, status: 'accepted', reviewed_at: new Date().toISOString() } : r
      ));
    } catch (err) {
      console.error('Error accepting report:', err);
      alert('Failed to accept report');
    }

    setProcessing(null);
  };

  // Decline report
  const handleDecline = async (report) => {
    setProcessing(report.id);

    try {
      // Update report status
      await supabase
        .from('reports')
        .update({ status: 'declined', reviewed_at: new Date().toISOString() })
        .eq('id', report.id);

      // Unhide the card
      if (report.reported_card_id) {
        await supabase
          .from('cards')
          .update({ hidden: false })
          .eq('id', report.reported_card_id);
      }

      // Update local state
      setReports(prev => prev.map(r =>
        r.id === report.id ? { ...r, status: 'declined', reviewed_at: new Date().toISOString() } : r
      ));
    } catch (err) {
      console.error('Error declining report:', err);
      alert('Failed to decline report');
    }

    setProcessing(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReasonLabel = (reason) => {
    const labels = {
      nsfw: 'NSFW Content',
      wrong_content: 'Wrong Content',
      spam: 'Spam',
      scam: 'Scam',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
    },
    header: {
      backgroundColor: theme.bgCard,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      borderBottom: `1px solid ${theme.border}`,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    backButton: {
      background: 'none',
      border: 'none',
      padding: 8,
      cursor: 'pointer',
      color: theme.accent,
      display: 'flex',
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    filterRow: {
      display: 'flex',
      gap: 8,
      padding: '12px 16px',
      overflowX: 'auto',
      borderBottom: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
    },
    filterBtn: {
      padding: '8px 16px',
      borderRadius: 20,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
      color: theme.textSecondary,
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    filterBtnActive: {
      backgroundColor: theme.accent,
      borderColor: theme.accent,
      color: '#fff',
    },
    content: {
      padding: 16,
    },
    emptyState: {
      textAlign: 'center',
      padding: 40,
      color: theme.textMuted,
    },
    reportCard: {
      backgroundColor: theme.bgCard,
      borderRadius: 12,
      border: `1px solid ${theme.border}`,
      marginBottom: 12,
      overflow: 'hidden',
    },
    reportHeader: {
      padding: 12,
      borderBottom: `1px solid ${theme.border}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    reportReason: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.text,
    },
    reportDate: {
      fontSize: 12,
      color: theme.textMuted,
    },
    reportBody: {
      padding: 12,
      display: 'flex',
      gap: 12,
    },
    cardImage: {
      width: 80,
      height: 112,
      borderRadius: 8,
      objectFit: 'cover',
      backgroundColor: theme.bgHover,
    },
    reportInfo: {
      flex: 1,
    },
    infoRow: {
      marginBottom: 8,
    },
    infoLabel: {
      fontSize: 11,
      color: theme.textMuted,
      textTransform: 'uppercase',
    },
    infoValue: {
      fontSize: 13,
      color: theme.text,
    },
    description: {
      fontSize: 13,
      color: theme.textSecondary,
      fontStyle: 'italic',
      marginTop: 8,
      padding: 8,
      backgroundColor: theme.bgHover,
      borderRadius: 6,
    },
    reportActions: {
      padding: 12,
      borderTop: `1px solid ${theme.border}`,
      display: 'flex',
      gap: 8,
    },
    acceptBtn: {
      flex: 1,
      padding: '10px 16px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: '#22c55e',
      color: '#fff',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    },
    declineBtn: {
      flex: 1,
      padding: '10px 16px',
      borderRadius: 8,
      border: `1px solid ${theme.error}`,
      backgroundColor: 'transparent',
      color: theme.error,
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    statusAccepted: {
      backgroundColor: 'rgba(34, 197, 94, 0.15)',
      color: '#22c55e',
    },
    statusDeclined: {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      color: '#ef4444',
    },
    statusPending: {
      backgroundColor: 'rgba(250, 204, 21, 0.15)',
      color: '#eab308',
    },
    strikeWarning: {
      fontSize: 11,
      color: theme.error,
      marginTop: 4,
    },
    loading: {
      textAlign: 'center',
      padding: 40,
      color: theme.textMuted,
    },
  };

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Checking permissions...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Report Review</h1>
      </div>

      {/* Filter tabs */}
      <div style={styles.filterRow}>
        {['pending', 'accepted', 'declined', 'all'].map((f) => (
          <button
            key={f}
            style={{
              ...styles.filterBtn,
              ...(filter === f ? styles.filterBtnActive : {}),
            }}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading reports...</div>
        ) : reports.length === 0 ? (
          <div style={styles.emptyState}>
            No {filter === 'all' ? '' : filter} reports found
          </div>
        ) : (
          reports.map((report) => (
            <div key={report.id} style={styles.reportCard}>
              {/* Header */}
              <div style={styles.reportHeader}>
                <span style={styles.reportReason}>{getReasonLabel(report.reason)}</span>
                <span style={styles.reportDate}>{formatDate(report.created_at)}</span>
              </div>

              {/* Body */}
              <div style={styles.reportBody}>
                {report.card && (
                  <img
                    src={report.card.digital_image_url || report.card.original_image_url}
                    alt={report.card.name}
                    style={styles.cardImage}
                  />
                )}
                <div style={styles.reportInfo}>
                  {report.card && (
                    <div style={styles.infoRow}>
                      <div style={styles.infoLabel}>Card</div>
                      <div style={styles.infoValue}>{report.card.name}</div>
                    </div>
                  )}
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Reported by</div>
                    <div style={styles.infoValue}>{report.reporter?.username || 'Unknown'}</div>
                  </div>
                  {report.cardOwner && (
                    <div style={styles.infoRow}>
                      <div style={styles.infoLabel}>Card Owner</div>
                      <div style={styles.infoValue}>
                        {report.cardOwner.username}
                        {report.cardOwner.report_strikes > 0 && (
                          <span style={styles.strikeWarning}>
                            {' '}({report.cardOwner.report_strikes} strike{report.cardOwner.report_strikes > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {report.status !== 'pending' && (
                    <div style={styles.infoRow}>
                      <div style={styles.infoLabel}>Status</div>
                      <span style={{
                        ...styles.statusBadge,
                        ...(report.status === 'accepted' ? styles.statusAccepted : styles.statusDeclined),
                      }}>
                        {report.status}
                      </span>
                    </div>
                  )}
                  {report.description && (
                    <div style={styles.description}>"{report.description}"</div>
                  )}
                </div>
              </div>

              {/* Actions (only for pending) */}
              {report.status === 'pending' && (
                <div style={styles.reportActions}>
                  <button
                    style={styles.acceptBtn}
                    onClick={() => handleAccept(report)}
                    disabled={processing === report.id}
                  >
                    <CheckIcon />
                    {processing === report.id ? 'Processing...' : 'Accept'}
                  </button>
                  <button
                    style={styles.declineBtn}
                    onClick={() => handleDecline(report)}
                    disabled={processing === report.id}
                  >
                    <XIcon />
                    Decline
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminReports;
